import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { Helmet } from 'react-helmet-async';
import { Calendar, Wrench, RotateCcw, Coins, TrendingUp, Building, Info, Calculator } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './EkonomiPage.css';
import { ComponentLifetime, TemplatesByType } from '../Förvaltning/TemplatesEditor/types';
import { Building as BuildingType } from '../../types/protocols';
import { BaseType } from '../../types/Enums';
import { BuildingType as BuildingTypeEnum } from '../../types/BuildingTypeEnums';
import { translate } from '../../types/EnumTranslations';
import { SERVICE_COLORS } from '../../Helpers/chartColors';
import COLORS from '../../Helpers/utils';
import CustomMultiSelect, { CustomMultiSelectOption } from '../../Components/CustomMultiSelect';
import { 
    generateYearlyPlansForBuilding, 
    aggregateYearlyPlans,
    YearlyActivity,
    MaintenancePlan
} from '../../Helpers/maintenanceCostUtils';
import MaintenanceCostsTab from './MaintenanceCostsTab';
import DepreciationTab from './DepreciationTab';

interface BuildingTypeOption {
    label: string;
    value: BuildingTypeEnum;
}

const EkonomiPage: React.FC = () => {
    const [buildings, setBuildings] = useState<Record<string, BuildingType>>({});
    const [activePlans, setActivePlans] = useState<Record<string, MaintenancePlan>>({});
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [componentConditions, setComponentConditions] = useState<Record<string, Record<string, any>>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [yearsToShow, setYearsToShow] = useState<number>(10);
    const [yearlyPlans, setYearlyPlans] = useState<YearlyActivity[]>([]);
    const [activeTab, setActiveTab] = useState<'maintenance' | 'depreciation'>('maintenance');
    
    // Preserve expanded states for depreciation tab
    const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set());
    const [expandedBaseTypes, setExpandedBaseTypes] = useState<Set<string>>(new Set());
    
    // Building type filter - Default to show all building types
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<BuildingTypeEnum[]>([
        BuildingTypeEnum.standard,
        BuildingTypeEnum.complement,
        BuildingTypeEnum.toiletArchipelago,
        BuildingTypeEnum.toiletReserve,
        BuildingTypeEnum.other
    ]);
    
    // Inflation adjustment - Default to false
    const [adjustForInflation, setAdjustForInflation] = useState<boolean>(false);

    const db = getDatabase();

    // Building type options for the filter dropdown
    const buildingTypeOptions: BuildingTypeOption[] = [
        { label: translate(BuildingTypeEnum.standard), value: BuildingTypeEnum.standard },
        { label: translate(BuildingTypeEnum.complement), value: BuildingTypeEnum.complement },
        { label: translate(BuildingTypeEnum.toiletArchipelago), value: BuildingTypeEnum.toiletArchipelago },
        { label: translate(BuildingTypeEnum.toiletReserve), value: BuildingTypeEnum.toiletReserve },
        { label: translate(BuildingTypeEnum.other), value: BuildingTypeEnum.other },
    ];


    // Load all required data
    const loadData = async (): Promise<void> => {
        try {
            setLoading(true);

            const [buildingsSnapshot, activePlansSnapshot, lifetimesSnapshot, templatesSnapshot, summariesSnapshot] = await Promise.all([
                get(ref(db, 'buildings')),
                get(ref(db, 'maintenance/active')),
                get(ref(db, 'maintenance/componentLifetimes')),
                get(ref(db, 'maintenance/templates')),
                get(ref(db, 'buildingsSummary'))
            ]);

            // Load buildings with summaries
            if (buildingsSnapshot.exists()) {
                const buildingsData = buildingsSnapshot.val() as Record<string, any>;
                const summariesData = summariesSnapshot.exists() ? summariesSnapshot.val() : {};
                const buildingsWithSummary: Record<string, BuildingType> = {};
                
                Object.entries(buildingsData).forEach(([buildingKey, buildingModel]) => {
                    buildingsWithSummary[buildingKey] = {
                        key: buildingKey,
                        model: buildingModel,
                        summary: summariesData[buildingKey] || { key: buildingKey }
                    };
                });

                setBuildings(buildingsWithSummary);
            }

            // Load active maintenance plans
            if (activePlansSnapshot.exists()) {
                const activePlansData = activePlansSnapshot.val() as Record<string, MaintenancePlan>;
                setActivePlans(activePlansData);
            }

            // Load component lifetimes
            if (lifetimesSnapshot.exists()) {
                const data = lifetimesSnapshot.val() as Record<string, ComponentLifetime>;
                setComponentLifetimes(data);
            }

            // Load maintenance templates
            if (templatesSnapshot.exists()) {
                const data = templatesSnapshot.val() as Record<string, Record<string, any>>;
                const templatesByType: TemplatesByType = {};
                
                Object.entries(data).forEach(([componentType, componentTemplates]) => {
                    if (componentTemplates && typeof componentTemplates === 'object') {
                        templatesByType[componentType] = Object.entries(componentTemplates).map(([id, template]: [string, any]) => ({
                            id,
                            name: template.name || '',
                            description: template.description || '',
                            interval_type: template.interval_type || 'years',
                            interval_value: template.interval_value || 1,
                            cost: template.cost ?? 0,
                            cost_unit: template.cost_unit ?? 'per_sqm',
                            directionalIntervals: template.directionalIntervals || undefined
                        }));
                    }
                });
                setMaintenanceTemplates(templatesByType);
            }

            // Load component conditions for all buildings with active plans
            const conditionsData: Record<string, Record<string, any>> = {};
            const buildingKeys = Object.keys(activePlans);
            
            if (buildingKeys.length > 0) {
                const conditionsPromises = buildingKeys.map(async (buildingKey) => {
                    const conditionsSnapshot = await get(ref(db, `componentConditions/${buildingKey}`));
                    if (conditionsSnapshot.exists()) {
                        conditionsData[buildingKey] = conditionsSnapshot.val();
                    } else {
                        conditionsData[buildingKey] = {};
                    }
                });

                await Promise.all(conditionsPromises);
            }

            setComponentConditions(conditionsData);

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Apply inflation adjustment to yearly plans based on when prices were set
    const applyInflationAdjustment = (plans: YearlyActivity[]): YearlyActivity[] => {
        if (!adjustForInflation) {
            return plans;
        }

        const inflationRate = 0.02; // 2% per year

        return plans.map(yearPlan => {
            // Apply inflation to all activities based on their price setting date
            const adjustedActivities = yearPlan.activities.map(activity => {
                // Get the price setting date for this activity
                const priceSettingDate = getPriceSettingDate(activity);
                const priceSettingYear = new Date(priceSettingDate).getFullYear();
                
                // Calculate years from price setting date to the activity year
                const yearsFromPriceSetting = yearPlan.year - priceSettingYear;
                const inflationMultiplier = Math.pow(1 + inflationRate, yearsFromPriceSetting);

                return {
                    ...activity,
                    cost: activity.cost * inflationMultiplier,
                    originalCost: activity.cost,
                    inflationYears: yearsFromPriceSetting,
                    inflationMultiplier: inflationMultiplier,
                    priceSettingYear: priceSettingYear
                };
            });

            // Recalculate costs with inflation
            const adjustedTotalCost = adjustedActivities.reduce((sum, activity) => sum + activity.cost, 0);
            
            // Recalculate costs by base type
            const adjustedCostsByBaseType: Record<string, number> = {};
            adjustedActivities.forEach(activity => {
                const baseType = activity.baseType;
                adjustedCostsByBaseType[baseType] = (adjustedCostsByBaseType[baseType] || 0) + activity.cost;
            });

            return {
                ...yearPlan,
                activities: adjustedActivities,
                totalCost: adjustedTotalCost,
                costsByBaseType: adjustedCostsByBaseType
            };
        });
    };

    // Helper function to get the price setting date for an activity
    // This ensures inflation is calculated from when the price was actually set,
    // not from the current year, providing more accurate cost projections
    const getPriceSettingDate = (activity: any): string => {
        // For maintenance activities, use the lastUpdated timestamp from the maintenance plan
        if (activity.activityType === 'maintenance' && activity.templateId) {
            const buildingKey = activity.buildingKey;
            const componentKey = activity.componentKey;
            const templateId = activity.templateId;
            
            if (buildingKey && activePlans[buildingKey]?.components[componentKey]?.maintenanceActivities?.[templateId]?.lastUpdated) {
                return activePlans[buildingKey].components[componentKey].maintenanceActivities[templateId].lastUpdated;
            }
        }
        
        // For replacement activities, use the plan's savedAt date
        if (activity.activityType === 'replacement' && activity.buildingKey) {
            const buildingKey = activity.buildingKey;
            if (activePlans[buildingKey]?.metadata?.savedAt) {
                return activePlans[buildingKey].metadata.savedAt;
            }
        }
        
        // Fallback to current date if no timestamp is available
        return new Date().toISOString();
    };

    // Generate aggregated yearly plans from all active maintenance plans
    const generateAggregatedYearlyPlans = (): void => {
        if (!Object.keys(buildings).length || !Object.keys(activePlans).length || !Object.keys(componentLifetimes).length || !Object.keys(maintenanceTemplates).length) {
            return;
        }

        const allBuildingPlans: YearlyActivity[][] = [];

        // Generate plans for each building with an active plan
        Object.entries(activePlans).forEach(([buildingKey, activePlan]) => {
            const building = buildings[buildingKey];
            if (!building?.model?.structure?.components) return;

            // Filter by selected building types
            if (building.summary?.buildingType && !selectedBuildingTypes.includes(building.summary.buildingType)) {
                return;
            }

            const buildingName = building.summary?.name || 'Namnlös byggnad';
            const buildingConditions = componentConditions[buildingKey] || {};

            const buildingPlans = generateYearlyPlansForBuilding({
                building,
                plan: activePlan,
                componentLifetimes,
                maintenanceTemplates,
                componentConditions: buildingConditions,
                yearsToShow,
                buildingName
            });

            if (buildingPlans.length > 0) {
                allBuildingPlans.push(buildingPlans);
            }
        });

        // Aggregate all building plans
        const aggregatedPlans = aggregateYearlyPlans(allBuildingPlans);
        
        // Apply inflation adjustment if enabled
        const finalPlans = applyInflationAdjustment(aggregatedPlans);
        
        setYearlyPlans(finalPlans);
    };

    // Load data when component mounts
    useEffect(() => {
        loadData();
    }, []);

    // Initialize Bootstrap tooltips
    useEffect(() => {
        const initializeTooltips = () => {
            // Check if Bootstrap is available
            if (typeof window !== 'undefined' && (window as any).bootstrap && (window as any).bootstrap.Tooltip) {
                // Initialize tooltips when component mounts or updates
                const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
                const tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
                    return new (window as any).bootstrap.Tooltip(tooltipTriggerEl, {
                        container: 'body', // This ensures tooltips are appended to body, avoiding clipping
                        boundary: 'viewport'
                    });
                });

                return tooltipList;
            }
            return [];
        };

        // Try to initialize immediately
        let tooltipList = initializeTooltips();
        
        // If Bootstrap isn't available yet, wait a bit and try again
        if (tooltipList.length === 0) {
            const timeoutId = setTimeout(() => {
                tooltipList = initializeTooltips();
            }, 100);
            
            return () => {
                clearTimeout(timeoutId);
                tooltipList.forEach(tooltip => tooltip.dispose());
            };
        }

        // Cleanup tooltips when component unmounts
        return () => {
            tooltipList.forEach(tooltip => tooltip.dispose());
        };
    }, [yearlyPlans]); // Re-initialize when yearly plans change

    // Generate yearly plans when dependencies change
    useEffect(() => {
        generateAggregatedYearlyPlans();
    }, [buildings, activePlans, componentLifetimes, maintenanceTemplates, componentConditions, yearsToShow, selectedBuildingTypes, adjustForInflation]);

    if (loading) {
        return (
            <div className="ekonomi-page">
        <Helmet>
                    <title>Ekonomi - Underhållskostnader</title>
        </Helmet>
                <div className="loading-container">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
        </div>
    </div>
);
    }

    const totalActivities = yearlyPlans.reduce((total, year) => total + year.activities.length, 0);
    const totalCost = yearlyPlans.reduce((total, year) => total + year.totalCost, 0);
    
    // Count only buildings that match the selected building types and have active plans
    const activeBuildingsCount = Object.keys(activePlans).filter(buildingKey => {
        const building = buildings[buildingKey];
        return building?.summary?.buildingType && selectedBuildingTypes.includes(building.summary.buildingType);
    }).length;

    return (
        <div className="ekonomi-page">
            <Helmet>
                <title>Ekonomi</title>
            </Helmet>

            {/* Tab Navigation - Full Width at Top */}
            <nav className="navbar navbar-expand-lg navbar-light bg-light ekonomi-navbar">
                <div className="container-fluid">
                    <div className="navbar-brand ekonomi-brand">
                        <h1 className="ekonomi-title">Ekonomi</h1>
                        <p className="ekonomi-description">Ekonomiska analyser och prognoser för byggnader</p>
                    </div>
                    <div className="navbar-nav ms-auto">
                        <button
                            className={`nav-link ekonomi-nav-link ${activeTab === 'maintenance' ? 'active' : ''}`}
                            onClick={() => setActiveTab('maintenance')}
                            type="button"
                        >
                            Underhållskostnader
                        </button>
                        <button
                            className={`nav-link ekonomi-nav-link ${activeTab === 'depreciation' ? 'active' : ''}`}
                            onClick={() => setActiveTab('depreciation')}
                            type="button"
                        >
                            Avskrivningar
                        </button>
                    </div>
                </div>
            </nav>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'maintenance' && (
                    <MaintenanceCostsTab
                        buildings={buildings}
                        activePlans={activePlans}
                        componentLifetimes={componentLifetimes}
                        maintenanceTemplates={maintenanceTemplates}
                        componentConditions={componentConditions}
                        loading={loading}
                        yearsToShow={yearsToShow}
                        setYearsToShow={setYearsToShow}
                        yearlyPlans={yearlyPlans}
                        selectedBuildingTypes={selectedBuildingTypes}
                        setSelectedBuildingTypes={setSelectedBuildingTypes}
                        adjustForInflation={adjustForInflation}
                        setAdjustForInflation={setAdjustForInflation}
                        buildingTypeOptions={buildingTypeOptions}
                    />
                )}
                
                {activeTab === 'depreciation' && (
                    <DepreciationTab
                        buildings={buildings}
                        componentLifetimes={componentLifetimes}
                        activePlans={activePlans}
                        onDataRefresh={loadData}
                        expandedBuildings={expandedBuildings}
                        setExpandedBuildings={setExpandedBuildings}
                        expandedBaseTypes={expandedBaseTypes}
                        setExpandedBaseTypes={setExpandedBaseTypes}
                    />
                )}
            </div>
        </div>
    );
};

export default EkonomiPage;
